import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Home, MapPin, Ruler, Bed, Bath, DollarSign, Compass, Focus, Maximize2, Eye, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Lot } from '../../types';
import { useTheme } from '../styles/ThemeContext';
interface LotPreviewCardProps {
  lot: Lot;
  onClose: () => void;
  onCustomize: () => void;
  onRecenter?: () => void;
  on360View?: () => void;
  onVisit?: () => void;
}
// Updated with verified working Unsplash images
const CAROUSEL_IMAGES = ['https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=600&q=80', 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=600&q=80', 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=600&q=80', 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80'];
export function LotPreviewCard({
  lot,
  onClose,
  onCustomize,
  onRecenter,
  on360View,
  onVisit
}: LotPreviewCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const {
    isDark
  } = useTheme();
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);
  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev + 1) % CAROUSEL_IMAGES.length);
  };
  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev - 1 + CAROUSEL_IMAGES.length) % CAROUSEL_IMAGES.length);
  };
  return <div className="absolute inset-0 pointer-events-none z-50" data-id="element-850">
      <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 300
    }} className={`absolute bottom-0 left-[420px] backdrop-blur-xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col transition-all duration-300 ${isDark ? 'bg-slate-900/70 border-t-[3px] border-cyan-500' : 'bg-white/70 border-t-[3px] border-teal-600 shadow-xl'}`} style={{
      width: 'fit-content'
    }} data-id="element-851">
        {/* Header Bar */}
        <div className={`relative px-3 py-2 flex items-center justify-between w-full transition-colors duration-300 ${isDark ? 'bg-slate-800/50 border-b border-white/10' : 'bg-white/50 border-b border-white/40'}`} data-id="element-852">
          <div className="flex items-center gap-3" data-id="element-853">
            <div className={`p-1.5 rounded shadow-lg transition-colors duration-300 ${isDark ? 'bg-cyan-500 text-white shadow-cyan-500/20' : 'bg-teal-600 text-white shadow-teal-600/20'}`} data-id="element-854">
              <Home className="w-3.5 h-3.5" data-id="element-855" />
            </div>
            <div className="flex items-baseline gap-2" data-id="element-856">
              <span className={`text-sm font-light tracking-wide transition-colors duration-300 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-857">
                LOT <span className="font-bold" data-id="element-858">{lot.id}</span>
              </span>
              <span className={`text-[10px] font-bold uppercase tracking-wider ${lot.status === 'available' ? isDark ? 'text-cyan-400' : 'text-teal-600' : lot.status === 'pending' ? 'text-yellow-400' : isDark ? 'text-slate-400' : 'text-gray-400'}`} data-id="element-859">
                {lot.status}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1" data-id="element-860">
            {onRecenter && <TooltipButton onClick={onRecenter} tooltip="Recenter" icon={<Focus className="w-3.5 h-3.5" data-id="element-862" />} isDark={isDark} data-id="element-861" />}
            {on360View && <TooltipButton onClick={on360View} tooltip="360° View" icon={<Maximize2 className="w-3.5 h-3.5" data-id="element-864" />} isDark={isDark} data-id="element-863" />}
            {onVisit && <TooltipButton onClick={onVisit} tooltip="Interior Visit" icon={<Eye className="w-3.5 h-3.5" data-id="element-866" />} isDark={isDark} data-id="element-865" />}
            <TooltipButton onClick={() => {}} tooltip="Search" icon={<Search className="w-3.5 h-3.5" data-id="element-868" />} isDark={isDark} data-id="element-867" />
            <TooltipButton onClick={onClose} tooltip="Close" icon={<X className="w-3.5 h-3.5" data-id="element-870" />} isDark={isDark} data-id="element-869" />
          </div>
        </div>

        {/* Main Content - Horizontal Layout */}
        <div className="flex h-[180px]" data-id="element-871">
          {/* Left: Lot Details (180px) */}
          <div className={`w-[180px] px-3 py-3 space-y-1 transition-colors duration-300 ${isDark ? 'border-r border-white/10 bg-slate-900/30' : 'border-r border-white/40 bg-white/30'}`} data-id="element-872">
            <DetailRow icon={<Home className="w-3 h-3" data-id="element-874" />} label="BLOCK" value={lot.block} isDark={isDark} data-id="element-873" />
            <DetailRow icon={<MapPin className="w-3 h-3" data-id="element-876" />} label="LOT" value={lot.id.toString()} isDark={isDark} data-id="element-875" />
            <DetailRow icon={<Home className="w-3 h-3" data-id="element-878" />} label="MODEL" value="Elevation A" isDark={isDark} data-id="element-877" />
            <DetailRow icon={<Ruler className="w-3 h-3" data-id="element-880" />} label="SURFACE" value={lot.sqft.toLocaleString()} isDark={isDark} data-id="element-879" />
            <DetailRow icon={<Bed className="w-3 h-3" data-id="element-882" />} label="BEDS" value="4" isDark={isDark} data-id="element-881" />
            <DetailRow icon={<Bath className="w-3 h-3" data-id="element-884" />} label="BATHS" value="2" isDark={isDark} data-id="element-883" />
            <DetailRow icon={<DollarSign className="w-3 h-3" data-id="element-886" />} label="PRICE" value={`${(lot.price / 1000).toFixed(0)}k`} isDark={isDark} data-id="element-885" />
            <DetailRow icon={<Compass className="w-3 h-3" data-id="element-888" />} label="FACING" value={lot.facing} isDark={isDark} data-id="element-887" />
          </div>

          {/* Center: Floor Plan (180px) */}
          <div className={`w-[180px] flex items-center justify-center p-4 relative transition-colors duration-300 ${isDark ? 'border-r border-white/10 bg-slate-800/20' : 'border-r border-white/40 bg-white/20'}`} data-id="element-889">
            <svg viewBox="0 0 200 200" className="w-full h-auto max-h-[140px] drop-shadow-lg" data-id="element-890">
              {/* Outer walls */}
              <rect x="30" y="30" width="140" height="140" fill="none" stroke={isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)'} strokeWidth="2.5" data-id="element-891" />

              {/* Interior walls - horizontal */}
              <line x1="30" y1="100" x2="170" y2="100" stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)'} strokeWidth="2" strokeDasharray="4,2" data-id="element-892" />

              {/* Interior walls - vertical */}
              <line x1="100" y1="30" x2="100" y2="100" stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)'} strokeWidth="2" strokeDasharray="4,2" data-id="element-893" />
              <line x1="135" y1="100" x2="135" y2="170" stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)'} strokeWidth="2" strokeDasharray="4,2" data-id="element-894" />

              {/* Doors */}
              <line x1="70" y1="30" x2="90" y2="30" stroke={isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)'} strokeWidth="2.5" data-id="element-895" />
              <line x1="100" y1="65" x2="100" y2="85" stroke={isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)'} strokeWidth="2.5" data-id="element-896" />
              <line x1="135" y1="125" x2="135" y2="145" stroke={isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)'} strokeWidth="2.5" data-id="element-897" />
            </svg>
          </div>

          {/* Right: Image Carousel (200px) */}
          <div className={`w-[200px] relative h-full group overflow-hidden ${isDark ? 'bg-slate-900' : 'bg-gray-100'}`} data-id="element-898">
            <AnimatePresence mode="wait" data-id="element-899">
              <motion.img key={currentImageIndex} src={CAROUSEL_IMAGES[currentImageIndex]} alt={`View ${currentImageIndex + 1}`} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.5
            }} className="absolute inset-0 w-full h-full object-cover" data-id="element-900" />
            </AnimatePresence>

            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent' : 'bg-gradient-to-t from-white/80 via-white/10 to-transparent'}`} data-id="element-901" />

            {/* Carousel Controls */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between px-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-902">
              <button onClick={prevImage} className={`p-1 rounded-full transition-colors ${isDark ? 'bg-black/50 text-white hover:bg-black/70' : 'bg-white/70 text-gray-800 hover:bg-white/90'}`} data-id="element-903">
                <ChevronLeft className="w-4 h-4" data-id="element-904" />
              </button>
              <button onClick={nextImage} className={`p-1 rounded-full transition-colors ${isDark ? 'bg-black/50 text-white hover:bg-black/70' : 'bg-white/70 text-gray-800 hover:bg-white/90'}`} data-id="element-905">
                <ChevronRight className="w-4 h-4" data-id="element-906" />
              </button>
            </div>

            {/* Carousel Indicators */}
            <div className="absolute bottom-[52px] left-0 right-0 flex justify-center gap-1.5 z-10" data-id="element-907">
              {CAROUSEL_IMAGES.map((_, idx) => <div key={idx} className={`w-1 h-1 rounded-full transition-all ${idx === currentImageIndex ? isDark ? 'bg-white w-3' : 'bg-gray-800 w-3' : isDark ? 'bg-white/40' : 'bg-gray-400'}`} data-id="element-908" />)}
            </div>

            {/* Customize Button - Bottom Right */}
            {lot.status === 'available' && <div className="absolute bottom-3 right-3 left-3 z-20" data-id="element-909">
                <button onClick={onCustomize} className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded font-bold text-[10px] uppercase tracking-widest transition-all hover:shadow-lg active:scale-95 ${isDark ? 'bg-cyan-500 hover:bg-cyan-400 text-white hover:shadow-cyan-500/25' : 'bg-teal-600 hover:bg-teal-500 text-white hover:shadow-teal-600/25'}`} data-id="element-910">
                  Customize
                  <ArrowRight className="w-3 h-3" data-id="element-911" />
                </button>
              </div>}
          </div>
        </div>
      </motion.div>
    </div>;
}
interface TooltipButtonProps {
  onClick: () => void;
  tooltip: string;
  icon: React.ReactNode;
  isDark: boolean;
}
function TooltipButton({
  onClick,
  tooltip,
  icon,
  isDark
}: TooltipButtonProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  return <div className="relative inline-block" data-id="element-912">
      <button onClick={onClick} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className={`p-1.5 rounded transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400 hover:text-white' : 'hover:bg-white/60 text-gray-500 hover:text-gray-900'}`} aria-label={tooltip} data-id="element-913">
        {icon}
      </button>
      {showTooltip && <motion.div initial={{
      opacity: 0,
      y: 5
    }} animate={{
      opacity: 1,
      y: 0
    }} className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-[10px] font-medium rounded shadow-xl whitespace-nowrap pointer-events-none z-[9999] ${isDark ? 'bg-slate-900 text-white border border-white/10' : 'bg-white text-gray-900 border border-gray-200'}`} data-id="element-914">
          {tooltip}
        </motion.div>}
    </div>;
}
interface DetailRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  isDark: boolean;
}
function DetailRow({
  icon,
  label,
  value,
  isDark
}: DetailRowProps) {
  return <div className="flex items-center gap-2" data-id="element-915">
      <span className={isDark ? 'text-slate-500' : 'text-gray-400'} data-id="element-916">
        {icon}
      </span>
      <div className="flex-1 flex items-center justify-between" data-id="element-917">
        <span className={`text-[9px] uppercase tracking-widest font-semibold ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-918">
          {label}
        </span>
        <span className={`text-[10px] font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-919">
          {value}
        </span>
      </div>
    </div>;
}